import axios from 'axios';

// Production API
const MFU_USER_API_BASE = 'https://mfuwellnesscenter-server.dietz.asia/api/mfu-user';

// Local Development API
// const MFU_USER_API_BASE = 'http://localhost/full-mfu/mfu-backend/api/mfu-user';

/**
 * Get user's NID from localStorage
 * @returns {string|null} User's NID (National ID)
 */
const getUserNID = () => {
  try {
    // ลองดึงจาก username ก่อน (เก็บ NID จริง)
    const username = localStorage.getItem('username');
    if (username) {
      return username;
    }

    // ถ้าไม่มี username ให้ลองดึงจาก userData
    const userData = localStorage.getItem('userData');
    if (userData) {
      try {
        const data = JSON.parse(userData);
        return data?.id || null;
      } catch {
        // Parse ไม่ได้
      }
    }

    return null;
  } catch (error) {
    console.error('Error getting user NID:', error);
    return null;
  }
};

/**
 * Get health recommendations for user
 * @param {string} nid - National ID (optional, will use from localStorage if not provided)
 * @returns {Promise} Response with recommendations
 */
export const getRecommendations = async (nid = null, visitDate = null) => {
  try {
    const userNID = nid || getUserNID();
    if (!userNID) {
      throw new Error('NID not found. Please login again.');
    }

    console.log('📋 Getting recommendations for NID:', userNID, 'visitDate:', visitDate);

    const body = { nid: userNID };
    if (visitDate) body.visit_date = visitDate;

    const response = await axios.post(`${MFU_USER_API_BASE}/recommendations`, body, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    console.log('✅ Recommendations response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error getting recommendations:', error);
    throw error;
  }
};

/**
 * Get notification history for user
 * @param {string} nid - National ID (optional, will use from localStorage if not provided)
 * @returns {Promise} Response with notifications
 */
export const getNotifications = async (nid = null) => {
  try {
    const userNID = nid || getUserNID();
    if (!userNID) {
      throw new Error('NID not found. Please login again.');
    }

    console.log('🔔 Getting notifications for NID:', userNID);

    const response = await axios.post(`${MFU_USER_API_BASE}/notifications`, {
      nid: userNID
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    console.log('✅ Notifications response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error getting notifications:', error);
    throw error;
  }
};

/**
 * Get unread notification count (for bell icon badge)
 * @param {string} nid - National ID (optional, will use from localStorage if not provided)
 * @returns {Promise} Response with unread_count
 */
export const getUnreadNotificationCount = async (nid = null) => {
  try {
    const userNID = nid || getUserNID();
    if (!userNID) {
      throw new Error('NID not found. Please login again.');
    }

    console.log('🔔 Getting unread notification count for NID:', userNID);

    const response = await axios.post(`${MFU_USER_API_BASE}/notifications/unread-count`, {
      nid: userNID
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    console.log('✅ Unread count response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error getting unread notification count:', error);
    throw error;
  }
};

/**
 * Mark all notifications as read (when user clicks bell icon)
 * @param {string} nid - National ID (optional, will use from localStorage if not provided)
 * @returns {Promise} Response with marked_count
 */
export const markNotificationsAsRead = async (nid = null) => {
  try {
    const userNID = nid || getUserNID();
    if (!userNID) {
      throw new Error('NID not found. Please login again.');
    }

    console.log('✔️ Marking notifications as read for NID:', userNID);

    const response = await axios.post(`${MFU_USER_API_BASE}/notifications/mark-read`, {
      nid: userNID
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    console.log('✅ Mark as read response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error marking notifications as read:', error);
    throw error;
  }
};

/**
 * Get PDF download URL for health report
 * @param {number} historyId - History ID from notification
 * @returns {string} PDF download URL
 */
export const getPDFDownloadUrl = (historyId) => {
  return `${MFU_USER_API_BASE}/download-pdf/${historyId}`;
};

/**
 * Download PDF health report
 * @param {number} historyId - History ID from notification
 * @returns {Promise} Response with PDF content
 */
export const downloadPDF = async (historyId) => {
  try {
    console.log('📄 Downloading PDF for history ID:', historyId);

    const url = getPDFDownloadUrl(historyId);

    // Open PDF in new window
    window.open(url, '_blank');

    return { success: true };
  } catch (error) {
    console.error('❌ Error downloading PDF:', error);
    throw error;
  }
};

/**
 * Create share token for public health report (MFU User / LINE OA path)
 * Uses history_id to look up visit_date from DB directly — more reliable than checkup_date from notification
 * @param {string} nid - National ID
 * @param {number} historyId - health_recommendations_history.id (notification.id)
 * @param {number} expiresInHours - Token expiration time in hours (default: 24)
 * @returns {Promise} Response with share_url
 */
export const createShareTokenMFUUser = async (nid, historyId = null, expiresInHours = 24) => {
  try {
    console.log('🔗 Creating MFU User share token for NID:', nid, 'historyId:', historyId);

    const body = { nid, expires_in_hours: expiresInHours };
    if (historyId) body.history_id = historyId;

    const response = await axios.post(`${MFU_USER_API_BASE}/create-share-token`, body, {
      headers: { 'Content-Type': 'application/json' }
    });

    console.log('✅ MFU User share token response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error creating MFU User share token:', error);
    throw error;
  }
};

/**
 * Create share token for public health report (HR path — kept for reference, not used by LINE OA)
 * @param {string} nid - National ID
 * @param {number} expiresInHours - Token expiration time in hours (default: 24)
 * @returns {Promise} Response with share_url
 */
export const createShareToken = async (nid, expiresInHours = 24, visitDate = null) => {
  try {
    console.log('🔗 Creating share token for NID:', nid);

    const body = { nid, expires_in_hours: expiresInHours };
    if (visitDate) body.visit_date = visitDate;

    const response = await axios.post('https://mfuwellnesscenter-server.dietz.asia/api/admin-hr/create-share-token', body, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    console.log('✅ Share token response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error creating share token:', error);
    throw error;
  }
};

/**
 * Get health record detail from notification
 * This extracts the full health record data from notification data
 * @param {object} notification - Notification object from getNotifications()
 * @returns {object} Health record data formatted for HealthReportModal
 */
export const getHealthRecordFromNotification = (notification) => {
  if (!notification) {
    throw new Error('Notification data is required');
  }

  // แปลงข้อมูลจาก notification ให้เป็นรูปแบบที่ modal ต้องการ
  const record = {
    id: notification.id,
    hn: notification.hn || '-',
    nid: notification.nid || '-',
    firstname: notification.firstname || '-',
    lastname: notification.lastname || '-',
    gender: notification.gender || '-',
    age: notification.age || 0,
    checkupDate: notification.checkup_date || notification.sent_at || '-',

    // ข้อมูลสุขภาพพื้นฐาน
    height: notification.height || '-',
    weight: notification.weight || '-',
    bmi: notification.bmi || '-',
    waistline: notification.waistline || '-',
    systolic_bp: notification.systolic_bp || '-',
    diastolic_bp: notification.diastolic_bp || '-',
    pulse: notification.pulse || '-',

    // คำแนะนำจากแพทย์
    recommendations: notification.recommendations || {
      doctor_advice: notification.doctor_advice || '-',
      nutrition_advice: notification.nutrition_advice || '-',
      exercise_advice: notification.exercise_advice || '-',
      sleep_advice: notification.sleep_advice || '-'
    },

    // ข้อมูลการส่ง
    sent_by: notification.sent_by || '-',
    sent_at: notification.sent_at || '-',

    // Parameters (ถ้ามี)
    parameters: notification.parameters || [],

    // Status
    approvalStatus: 'approved' // ถ้าส่งมาแล้วถือว่า approved
  };

  return record;
};
